import { useState } from 'react';
import { Wallet, ArrowRight, Shield, Zap, Globe, Check, ExternalLink } from 'lucide-react';
import './ConnectWalletPage.css';

const ConnectWalletPage = () => {
  const [connecting, setConnecting] = useState(null);
  const [connected, setConnected] = useState(false);

  const wallets = [
    { id: 'metamask', name: 'MetaMask', icon: '🦊', popular: true },
    { id: 'trust', name: 'Trust Wallet', icon: '🔵', popular: true },
    { id: 'ledger', name: 'Ledger', icon: '📱', popular: false },
    { id: 'trezor', name: 'Trezor', icon: '🔒', popular: false },
    { id: 'coinbase', name: 'Coinbase Wallet', icon: '🔷', popular: true },
    { id: 'phantom', name: 'Phantom', icon: '👻', popular: false },
  ];

  const benefits = [
    { icon: Shield, title: 'Self-Custody', desc: 'You maintain full control of your private keys' },
    { icon: Zap, title: 'One-Click Trading', desc: 'Connect and trade without manual address entry' },
    { icon: Globe, title: 'Multi-Chain', desc: 'Support for Ethereum, BSC, Polygon, and more' },
  ];

  const handleConnect = (walletId) => {
    setConnecting(walletId);
    setTimeout(() => {
      setConnecting(null);
      setConnected(true);
    }, 1500);
  };

  if (connected) {
    return (
      <div className="wallet-page">
        <div className="wallet-container">
          <div className="connected-state">
            <div className="success-icon">
              <Check size={48} />
            </div>
            <h1>Wallet Connected!</h1>
            <p>Your MetaMask wallet is now connected to Omini Bridge</p>
            <div className="wallet-address">0x71C7...8976F</div>
            <div className="connected-actions">
              <button className="primary-action">
                Start Trading
                <ArrowRight size={18} />
              </button>
              <button className="secondary-action" onClick={() => setConnected(false)}>
                Disconnect
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="wallet-page">
      {/* Background */}
      <div className="wallet-bg">
        <div className="wallet-grid"></div>
        <div className="wallet-glow"></div>
      </div>

      <div className="wallet-container">
        {/* Hero */}
        <section className="wallet-hero">
          <div className="wallet-icon-large">
            <Wallet size={48} />
          </div>
          <h1 className="wallet-title">
            Connect Your <span className="gradient-text">Wallet</span>
          </h1>
          <p className="wallet-subtitle">
            Connect your wallet to unlock faster trading, view your portfolio, and access exclusive features.
          </p>
        </section>

        {/* Wallet Grid */}
        <section className="wallets-section">
          <div className="wallets-grid">
            {wallets.map((wallet) => (
              <button
                key={wallet.id}
                className="wallet-option"
                onClick={() => handleConnect(wallet.id)}
                disabled={connecting !== null}
              >
                {wallet.popular && <span className="popular-badge">Popular</span>}
                <span className="wallet-icon-temp">{wallet.icon}</span>
                <span className="wallet-name">{wallet.name}</span>
                {connecting === wallet.id ? (
                  <span className="connecting-spinner"></span>
                ) : (
                  <ArrowRight size={18} className="wallet-arrow" />
                )}
              </button>
            ))}
          </div>
          <p className="wallet-hint">
            Don&apos;t have a wallet?{' '}
            <a href="#" className="create-wallet-link">
              Create one now <ExternalLink size={14} />
            </a>
          </p>
        </section>

        {/* Benefits */}
        <section className="wallet-benefits">
          <h2>Why Connect a Wallet?</h2>
          <div className="benefits-row">
            {benefits.map((benefit, index) => (
              <div key={index} className="benefit-box" style={{ animationDelay: `${index * 0.1}s` }}>
                <benefit.icon size={28} />
                <h3>{benefit.title}</h3>
                <p>{benefit.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Security Note */}
        <section className="security-note">
          <Shield size={24} />
          <div>
            <h3>Your Security is Our Priority</h3>
            <p>We never store your private keys or have access to your funds. All connections are encrypted and secure.</p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ConnectWalletPage;
